import { useMemo } from 'react';
import { Content, Stack, Strong, Text } from '@kinesis/bungle';
import { Table, TableColumn } from '@kinesis/bungle/nursery';
import { DisclosureLocationPage, SourceType } from '@/types';
import { renderSourceType } from '@/data/labels';
import { renderUnit, renderValue } from '@/data/units';
import { sourceColors } from '@/data/colors';
import { DisclosureStackedColumn } from '@/components/disclosure-stacked-column';

// One row of the matrix: a sub-location plus its keyed source-type cells. The
// wire `columns` (keyed `{ column, value }`, `null` = absent) is carried as-is
// so each column pulls its own cell by key — never by position.
type LocationRow = {
  key: string;
  name: string;
  columns: DisclosureLocationPage['table']['rows'][number]['columns'];
};

// Renders the "Emissions by location" page: a sub-location × source-type CO₂-e
// matrix in two arrangements over one server-built dataset — a stacked column
// (x-axis = sub-location, stack = source type) and the matrix beneath as an
// aligned data-table (rows = sub-locations, a source-type column each, a Total
// column, and a Total footer row). CO₂-e shares one unit across both (the FE
// never picks a unit); a cell is keyed by source type, so an absent source is
// "—", never a fabricated 0. The table is the bungle nursery `Table`, matching
// the Methodology page's emission-factor table.
//
// The one mapping the FE does is presentational: the keyed `chart.bars`
// (group-major) become Highcharts categories + series (segment-major), with
// `sourceColors` / `renderSourceType` for colour + label and the server's
// location names for the x-axis — the same "map the payload into the chart's
// shape" step disclosure-source does for the treemap, in the component layer
// (never the BFF), reshaping no values.
const DisclosureLocation = (props: DisclosureLocationProps) => {
  const { pageData } = props;
  const { unit, table, chart } = pageData;

  // The column axis: the source types in canonical order, taken from the
  // footer (which carries every present column).
  const sources: SourceType[] = useMemo(
    () => table.total.columns.map((c) => c.column),
    [table],
  );

  const { categories, series } = useMemo(() => {
    const cats = chart.bars.map((bar) => bar.group.name);
    const ser = sources.map((source) => ({
      name: renderSourceType(source),
      color: sourceColors[source],
      data: chart.bars.map((bar) => {
        const segment = bar.bar.segments.find((s) => s.segment === source);
        return segment ? segment.value : null;
      }),
    }));
    return { categories: cats, series: ser };
  }, [chart, sources]);

  // Bare numbers (the unit sits once at table scope); a null cell is "—".
  const cellText = (value: number | null) =>
    value === null ? '—' : renderValue(value, unit);
  const cellOf = (
    cells: LocationRow['columns'],
    source: SourceType,
  ): number | null =>
    cells.columns.find((c) => c.column === source)?.value ?? null;

  const rows: LocationRow[] = useMemo(
    () =>
      table.rows.map((r) => ({
        key: String(r.row.id),
        name: r.row.name,
        columns: r.columns,
      })),
    [table],
  );

  return (
    <Stack space='medium'>
      <Stack space='small'>
        <Strong>Emissions by location</Strong>
        <Text tone='secondary'>{renderUnit(unit)}</Text>
      </Stack>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <DisclosureStackedColumn
          categories={categories}
          series={series}
          unit={unit}
        />
      </Content>
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Table data={rows} itemKey='key' header footer showGridLines>
          <TableColumn
            heading='Location'
            width='1fr'
            renderItem={(row: LocationRow) => row.name}
            summary='Total'
          />
          {sources.map((source) => (
            <TableColumn
              key={source}
              heading={renderSourceType(source)}
              alignX='right'
              renderItem={(row: LocationRow) =>
                cellText(cellOf(row.columns, source))
              }
              summary={cellText(cellOf(table.total, source))}
            />
          ))}
          <TableColumn
            heading='Total'
            alignX='right'
            renderItem={(row: LocationRow) => cellText(row.columns.total)}
            summary={cellText(table.total.total)}
          />
        </Table>
      </Content>
    </Stack>
  );
};

export type DisclosureLocationProps = {
  pageData: DisclosureLocationPage;
};

export { DisclosureLocation };
